import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { AlertTriangle, CheckCircle2, Copy, CopyPlus, Lock, Pencil, Ticket, X } from 'lucide-react';
import moment from 'moment';

import { programTypes, getProgramType } from '../../../helper';
import { getCouponDetailService } from '../../../services/otherService';
import { ProgramLoader } from '../../reuse/SVG';
import {
    Field,
    FormSection,
    RadioCard,
    ReadOnlyValue,
    Select as NativeSelect,
    ToggleRow,
    errorInputClass,
    inputClass,
} from './ui/FormField';
import { COUPON_TYPES, TYPE_AMOUNT, TYPE_PERCENTAGE, formatDateRange, fromDetail } from './couponAdapter';
import {
    duplicateCode,
    duplicateName,
    findSimilarCoupon,
    formatCouponValue,
    generateCode,
} from './couponStatus';
import CouponSelect from './ui/CouponSelect';
import RangeInput from './ui/RangeInput';

/**
 * Create, edit and clone a coupon.
 *
 * Three modes. `create` and `clone` both post to `coupon/create`; `edit` posts to `coupon/edit`,
 * which deliberately accepts only six fields.
 *
 * Edit renders the same form as create with the price-bearing fields locked: value, type, code,
 * refund, user code and all targeting. They are shown, not hidden — a director needs to read the
 * current value to decide what to do about it — but they cannot be typed into, because
 * `cartFacade` re-prices open carts from the live coupon document on every render and changing one
 * would move the total under a family already in checkout.
 *
 * Consequently edit validates only the rules that govern the six unlocked fields. That is what
 * lets a coupon written years ago be edited at all: it can never trip a rule that postdates it,
 * because the fields those rules govern are locked. The one rule edit adds is the usage floor —
 * a limit below the redemptions already taken, which the server rejects too.
 *
 * Clone is the way past a locked field, and is just create with the form pre-filled: every field
 * is editable and every create rule applies, because the record it produces is new. It gets a new
 * name and a new code up front — two live coupons sharing a code is exactly what the redemption
 * counter cannot tell apart — and lands paused.
 *
 * The prototype's own MultiSelect is not ported. Programs, categories and sub-categories are real
 * records keyed by ObjectId, and react-select is what the rest of this app already uses to pick
 * them — `ui/selectStyles.js` restyles it to match the design rather than replacing it.
 */

const TEMPLATES = [
    {
        id: 'percentage',
        label: 'Percentage discount',
        description: 'A % off the session price',
        apply: { couponType: TYPE_PERCENTAGE, value: '10', possibleUsage: '100', perUserUsage: '1' },
    },
    {
        id: 'amount',
        label: 'Fixed amount',
        description: 'A flat dollar amount off',
        apply: { couponType: TYPE_AMOUNT, value: '50', possibleUsage: '100', perUserUsage: '1' },
    },
    {
        id: 'scholarship',
        label: 'Scholarship',
        description: 'High %, few uses, private',
        apply: {
            couponType: TYPE_PERCENTAGE,
            value: '75',
            possibleUsage: '1',
            perUserUsage: '1',
            isPrivate: true,
        },
    },
];

/**
 * Why a field is locked, in the words a director needs. Said once per locked group rather than on
 * every field — six copies of the same sentence reads as noise, not as an explanation.
 */
const LOCK_HINT = 'Locked — changing this would re-price carts already in checkout.';

const pill = label => (
    <span
        key={label}
        className="cpn-inline-flex cpn-items-center cpn-rounded-full cpn-bg-brand-50 cpn-px-2.5 cpn-py-1 cpn-text-xs cpn-font-semibold cpn-text-brand-700"
    >
        {label}
    </span>
);

const EMPTY_DRAFT = {
    name: '',
    code: '',
    codeEdited: false,
    couponType: '',
    value: '',
    startDate: '',
    endDate: '',
    scope: 'all',
    programs: [],
    experiences: [],
    category: null,
    subCategory: null,
    programType: '',
    userCode: '',
    possibleUsage: '',
    perUserUsage: '',
    isPrivate: true,
    refundable: false,
};

/**
 * Every rule blocks — and on the create path that is safe, because the data is new, so no rule can
 * be violated by a record that predates it.
 *
 * Edit is the case that would break that. A coupon written before the percentage cap existed, or
 * before a usage limit was required, would open with an error it did not cause and could not
 * clear. `locked` is what prevents it: in edit mode every rule below that governs a locked field
 * is skipped, because the director cannot change that field anyway. What survives is the five
 * rules covering the six editable fields — none of which can be failed by legacy data — plus the
 * usage floor, which only edit has.
 */
const validate = (draft, { locked = false, redeemed = 0 } = {}) => {
    const errors = {};
    const value = Number(draft.value);
    const possible = Number(draft.possibleUsage);
    const perUser = Number(draft.perUserUsage);

    if (!draft.name.trim()) errors.name = 'Give this coupon a name your team will recognise.';

    if (!locked) {
        // A code already used by another coupon is allowed, as it always was in the legacy form.
        // Directors rely on one code carrying a different price per program group, and
        // couponFacade.validateCoupon applies whichever matching coupon gives the bigger discount.
        if (!draft.code.trim()) errors.code = 'A code is required.';

        // The old form defaulted an unpicked type to Percentage, so leaving it blank quietly
        // created a "75% off" where "$75 off" was meant. Required instead.
        if (!draft.couponType) {
            errors.couponType = 'Choose whether this is a percentage or an amount.';
        }

        if (!String(draft.value).trim()) {
            errors.value = 'Enter a discount value.';
        } else if (Number.isNaN(value) || value <= 0) {
            errors.value = 'Value must be greater than 0.';
        } else if (draft.couponType === TYPE_PERCENTAGE && value > 100) {
            errors.value = 'A percentage cannot exceed 100%.';
        }
    }

    if (!draft.startDate) errors.startDate = 'Choose a start date.';
    if (!draft.endDate) {
        errors.endDate = 'Choose an end date.';
    } else if (draft.startDate && draft.endDate < draft.startDate) {
        errors.endDate = 'End date must be on or after the start date.';
    }

    if (!String(draft.possibleUsage).trim()) {
        errors.possibleUsage = 'Set the total number of redemptions allowed.';
    } else if (Number.isNaN(possible) || possible < 1) {
        errors.possibleUsage = 'Must be at least 1.';
    } else if (locked && possible < redeemed) {
        // couponFacade.edit rejects this too. Caught here so the director reads why in the field
        // rather than in a toast after a round-trip.
        errors.possibleUsage = `This code has already been redeemed ${redeemed} ${
            redeemed === 1 ? 'time' : 'times'
        }. The limit cannot be lower than that.`;
    }

    if (String(draft.perUserUsage).trim()) {
        if (Number.isNaN(perUser) || perUser < 1) {
            errors.perUserUsage = 'Must be at least 1.';
        } else if (!Number.isNaN(possible) && perUser > possible) {
            errors.perUserUsage = 'Cannot be more than the total usage limit.';
        }
    }

    if (!locked) {
        // couponFacade delivers a refund to the account named by userCode, so without one the
        // refund has nowhere to go.
        if (draft.refundable && !draft.userCode.trim()) {
            errors.userCode = 'A User Code is required when Refund is selected.';
        }

        if (
            draft.scope === 'specific' &&
            !draft.programs.length &&
            !draft.experiences.length &&
            !draft.category &&
            !draft.subCategory &&
            !draft.programType
        ) {
            errors.programs = 'Pick at least one program, experience, or category.';
        }
    }

    return errors;
};

const CouponFormModal = ({
    open,
    mode,
    coupon,
    allCoupons,
    programList,
    categories,
    submitting,
    deactivateOriginalByDefault,
    onClose,
    onCloneRequest,
    onSubmit,
}) => {
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [touched, setTouched] = useState({});
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [nudgeDismissed, setNudgeDismissed] = useState(false);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [deactivateOriginal, setDeactivateOriginal] = useState(false);

    // Whether the director has changed anything in the open form yet. The prefill effect below
    // must stop rebuilding the draft from `record` the moment this is true — otherwise an
    // unrelated reference change to `programList`/`categories`/`allCoupons` (another row's toggle
    // updates `allCoupons`, for instance) re-fires it mid-edit and silently reverts whatever was
    // just picked back to the source coupon's original targeting. Reset whenever the modal opens.
    const userEditedRef = useRef(false);

    const isEdit = mode === 'edit';
    const isClone = mode === 'clone';
    // Both open on an existing coupon and need the full record: edit to show and save it, clone to
    // copy it.
    const fromExisting = isEdit || isClone;

    // `detail` is stored alongside the id it was fetched for, and only counts for the coupon on
    // screen. That is what lets edit → clone on the same coupon reuse the record already loaded
    // (the fetch below does not re-run for it — same coupon, same record), while a record left
    // over from a previously opened coupon can never fill this one's form.
    //
    // A failed fetch settles too, with a null record, so "not fetched yet" and "could not load"
    // stay distinct: until the fetch for this coupon settles the dialog shows the loader.
    const detailSettled = Boolean(detail && coupon && detail.couponId === coupon.id);
    const record = detailSettled ? detail.record : null;
    const awaitingRecord = fromExisting && (detailLoading || !detailSettled);

    useEffect(() => {
        if (!open) return;
        setDraft(EMPTY_DRAFT);
        setTouched({});
        setSubmitAttempted(false);
        setNudgeDismissed(false);
        userEditedRef.current = false;
        // Pre-ticked when clone was reached from a locked field in the edit form — there the
        // director has already said they want this coupon to be different, so leaving the old one
        // live is almost never what they meant. Unticked from the row action, which is just as
        // often "make me another one like this".
        setDeactivateOriginal(Boolean(deactivateOriginalByDefault));
    }, [open, mode, coupon, deactivateOriginalByDefault]);

    /**
     * Edit and clone pull the full record. The list projection has the numbers but not the names —
     * category, sub-category, programs and experiences are ObjectIds there, and `getCouponData` is
     * what resolves them, alongside the raw ids the draft is rebuilt from.
     */
    useEffect(() => {
        if (!open || !fromExisting || !coupon) return undefined;

        let cancelled = false;
        setDetailLoading(true);

        getCouponDetailService(`?couponId=${coupon.id}`)
            .then(res => {
                if (cancelled) return;
                const result = res?.data?.responseData?.result?.[0];
                setDetail({ couponId: coupon.id, record: result || null });
            })
            // Settles with a null record, which is the "could not load" branch below.
            .catch(() => {
                if (!cancelled) setDetail({ couponId: coupon.id, record: null });
            })
            .finally(() => {
                if (!cancelled) setDetailLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, fromExisting, coupon]);

    /**
     * Edit and clone fill the form from the record once it arrives.
     *
     * Separate from the fetch so it also re-runs when `programList` or `categories` load after the
     * detail does — those are what turn the stored ObjectIds back into react-select options, and
     * on a cold page load they can land second. Without that dependency the targeting pickers
     * would come up empty on a coupon that has targeting.
     *
     * That same wide dependency list is exactly what would make this clobber a director's own
     * edits, though: `allCoupons` in particular changes reference whenever any row's toggle
     * flips, which has nothing to do with the coupon open in this form. `userEditedRef` is the
     * guard — once the director has touched anything, this effect stops rebuilding the draft
     * regardless of which dependency changed next.
     *
     * Clone additionally suggests a new name and a new code. The code is only a starting point: the
     * director can set it back to the source coupon's code, since several coupons may share one.
     */
    useEffect(() => {
        if (!open || !fromExisting || !record || userEditedRef.current) return;
        const next = fromDetail(record, { programList, categories });
        setDraft(
            isClone
                ? {
                      ...next,
                      name: duplicateName(next.name, allCoupons),
                      code: duplicateCode(next.code, allCoupons),
                  }
                : next
        );
    }, [open, fromExisting, isClone, record, programList, categories, allCoupons]);

    // Clone validates exactly like create — every field is new and editable.
    const errors = useMemo(
        () => validate(draft, { locked: isEdit, redeemed: coupon?.redeemed || 0 }),
        [draft, isEdit, coupon]
    );

    const showError = key => (submitAttempted || touched[key] ? errors[key] : undefined);
    const blur = key => () => setTouched(previous => ({ ...previous, [key]: true }));

    // Create only. In edit the closest match to the name is the coupon itself; in clone being a
    // near-duplicate is the entire intent. Either way the nudge would fire on every open and mean
    // nothing.
    const similar = useMemo(() => {
        if (isEdit || isClone || nudgeDismissed) return null;
        return findSimilarCoupon(draft.name, allCoupons);
    }, [isEdit, isClone, nudgeDismissed, draft.name, allCoupons]);

    // Every hand-made change to the draft goes through this rather than `setDraft` directly, so
    // `userEditedRef` reliably catches all of them — see the prefill effect above.
    const editDraft = updater => {
        userEditedRef.current = true;
        setDraft(updater);
    };

    const set = (key, value) => editDraft(previous => ({ ...previous, [key]: value }));

    // The code is what families type, and it is almost always the name with the spaces taken out.
    // Generated as you type, and left alone the moment it is edited by hand.
    const setName = name =>
        editDraft(previous => ({
            ...previous,
            name,
            code: previous.codeEdited ? previous.code : generateCode(name),
        }));

    const programOptions = useMemo(
        () => programList.map(item => ({ ...item, value: item._id, label: item.programName })),
        [programList]
    );

    // Only the experiences attached to the programs already picked — offering the rest would let a
    // director build a coupon that can never match anything.
    const experienceOptions = useMemo(
        () =>
            draft.programs
                .flatMap(program => program.additionalExp || [])
                .map(item => ({ value: item._id, label: item.title })),
        [draft.programs]
    );

    const categoryOptions = useMemo(
        () => (categories || []).map(item => ({ value: item._id, label: item.name })),
        [categories]
    );

    const subCategoryOptions = useMemo(() => {
        if (!draft.category) return [];
        const parent = (categories || []).find(item => item._id === draft.category.value);
        return (parent?.subCategory || []).map(item => ({ value: item._id, label: item.name }));
    }, [categories, draft.category]);

    const handleSubmit = event => {
        event.preventDefault();
        setSubmitAttempted(true);

        if (Object.keys(errors).length > 0) {
            const firstError = document.querySelector('[data-error="true"]');
            if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        // The second argument only means anything to `handleClone`; create and edit ignore it.
        onSubmit(draft, { deactivateOriginal });
    };

    const scopeSummary =
        draft.scope === 'all'
            ? 'all programs'
            : [...draft.programs, ...draft.experiences].map(item => item.label).slice(0, 3).join(', ') ||
              'selected programs';

    /* ---------- create and edit ---------- */

    const renderForm = () => (
        <div className="cpn-space-y-4">
            {similar && (
                <div className="cpn-flex cpn-flex-col cpn-gap-3 cpn-rounded-2xl cpn-border cpn-border-sky-200 cpn-bg-sky-50 cpn-p-4 sm:cpn-flex-row sm:cpn-items-center sm:cpn-justify-between">
                    <div className="cpn-flex cpn-items-start cpn-gap-3 cpn-text-sm cpn-text-sky-900">
                        <Copy className="cpn-mt-0.5 cpn-h-4 cpn-w-4 cpn-shrink-0 cpn-text-sky-600" aria-hidden="true" />
                        <p className="cpn-m-0">
                            You already have a coupon called <strong>{similar.name}</strong> (
                            <span className="cpn-font-mono">{similar.code}</span>). Make sure this one is
                            meant to be separate.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setNudgeDismissed(true)}
                        className="cpn-shrink-0 cpn-rounded-lg cpn-px-3 cpn-py-2 cpn-text-xs cpn-font-semibold cpn-text-sky-800 cpn-transition hover:cpn-bg-sky-100"
                    >
                        Got it
                    </button>
                </div>
            )}

            {isClone && (
                <div className="cpn-flex cpn-items-start cpn-gap-3 cpn-rounded-2xl cpn-border cpn-border-sky-200 cpn-bg-sky-50 cpn-p-4 cpn-text-sm cpn-text-sky-900">
                    <CopyPlus
                        className="cpn-mt-0.5 cpn-h-4 cpn-w-4 cpn-shrink-0 cpn-text-sky-600"
                        aria-hidden="true"
                    />
                    <p className="cpn-m-0">
                        Cloning{' '}
                        <span className="cpn-font-mono cpn-font-semibold">{coupon?.code}</span>. Every
                        field below is editable — this is a new coupon with its own code and its own
                        redemption count, so nothing you change here touches the original or any
                        booking made against it. It is created <strong>paused</strong> for you to
                        review.
                    </p>
                </div>
            )}

            {/* The way out of a locked field: the director has read the value, decided it is wrong,
                and this is the only thing that can change it. */}
            {isEdit && (
                <div className="cpn-flex cpn-flex-col cpn-gap-3 cpn-rounded-2xl cpn-border cpn-border-neutral-200 cpn-bg-white cpn-p-4 sm:cpn-flex-row sm:cpn-items-center sm:cpn-justify-between">
                    <div className="cpn-flex cpn-items-start cpn-gap-3 cpn-text-sm cpn-text-neutral-600">
                        <Lock
                            className="cpn-mt-0.5 cpn-h-4 cpn-w-4 cpn-shrink-0 cpn-text-neutral-400"
                            aria-hidden="true"
                        />
                        <p className="cpn-m-0">
                            Need to change the value, the code or what this applies to? Those are
                            locked here — clone this coupon and change them on the copy.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onCloneRequest}
                        className="cpn-inline-flex cpn-h-10 cpn-shrink-0 cpn-items-center cpn-justify-center cpn-gap-1.5 cpn-rounded-xl cpn-border cpn-border-neutral-200 cpn-px-4 cpn-text-xs cpn-font-semibold cpn-text-brand-700 cpn-transition hover:cpn-border-brand-300 hover:cpn-bg-brand-50"
                    >
                        <CopyPlus className="cpn-h-3.5 cpn-w-3.5" aria-hidden="true" />
                        Clone to change this
                    </button>
                </div>
            )}

            {/* This code is already out there. Redemptions are the signal that families are
                holding it, so the warning is keyed on those rather than on the coupon merely
                being active. */}
            {isEdit && coupon?.redeemed > 0 && (
                <div className="cpn-flex cpn-items-start cpn-gap-3 cpn-rounded-2xl cpn-border cpn-border-amber-200 cpn-bg-amber-50 cpn-p-4 cpn-text-sm cpn-text-amber-900">
                    <AlertTriangle
                        className="cpn-mt-0.5 cpn-h-4 cpn-w-4 cpn-shrink-0 cpn-text-amber-600"
                        aria-hidden="true"
                    />
                    <p className="cpn-m-0">
                        This code is in the market — it has been redeemed{' '}
                        <strong>
                            {coupon.redeemed} {coupon.redeemed === 1 ? 'time' : 'times'}
                        </strong>
                        . Bookings already made keep the discount they were given; changes here
                        affect future redemptions only.
                    </p>
                </div>
            )}

            {/* Templates set the value and the type: locked in edit, and already filled in from
                the source in clone. */}
            {!isEdit && !isClone && (
                <div className="cpn-rounded-2xl cpn-border cpn-border-neutral-200 cpn-bg-white cpn-p-5">
                    <h3 className="cpn-text-sm cpn-font-bold cpn-text-neutral-900">
                        Start from a template
                    </h3>
                    <p className="cpn-mt-0.5 cpn-text-xs cpn-text-neutral-500">
                        Optional — prefills the usual settings.
                    </p>
                    <div className="cpn-mt-4 cpn-grid cpn-gap-3 sm:cpn-grid-cols-3">
                        {TEMPLATES.map(template => (
                            <button
                                key={template.id}
                                type="button"
                                onClick={() =>
                                    editDraft(previous => ({ ...previous, ...template.apply }))
                                }
                                className="cpn-rounded-xl cpn-border cpn-border-neutral-200 cpn-p-3 cpn-text-left cpn-transition hover:cpn-border-brand-400 hover:cpn-bg-brand-50/50"
                            >
                                <span className="cpn-block cpn-text-sm cpn-font-semibold cpn-text-neutral-800">
                                    {template.label}
                                </span>
                                <span className="cpn-mt-0.5 cpn-block cpn-text-xs cpn-text-neutral-500">
                                    {template.description}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <FormSection title="Coupon details" description="Name it for your team, and set what it's worth.">
                <div className="cpn-grid cpn-gap-4 sm:cpn-grid-cols-2">
                    <div data-error={Boolean(showError('name'))}>
                        <Field id="coupon-name" label="Coupon Name" required error={showError('name')}>
                            <input
                                id="coupon-name"
                                value={draft.name}
                                onChange={event => setName(event.target.value)}
                                onBlur={blur('name')}
                                placeholder="e.g. Director Scholarship"
                                className={showError('name') ? errorInputClass : inputClass}
                            />
                        </Field>
                    </div>

                    <div data-error={Boolean(showError('code'))}>
                        {isEdit ? (
                            <Field id="coupon-code" label="Coupon Code" hint={LOCK_HINT}>
                                <ReadOnlyValue>
                                    <span className="cpn-font-mono">{draft.code}</span>
                                </ReadOnlyValue>
                            </Field>
                        ) : (
                            <Field
                                id="coupon-code"
                                label="Coupon Code"
                                required
                                error={showError('code')}
                                hint={
                                    draft.codeEdited
                                        ? 'What families type at checkout.'
                                        : 'Generated from the name — edit it if you need something different.'
                                }
                                action={
                                    !draft.codeEdited && (
                                        <button
                                            type="button"
                                            onClick={() => set('codeEdited', true)}
                                            className="cpn-inline-flex cpn-items-center cpn-gap-1 cpn-text-xs cpn-font-semibold cpn-text-brand-700 cpn-transition hover:cpn-text-brand-800"
                                        >
                                            <Pencil className="cpn-h-3 cpn-w-3" aria-hidden="true" />
                                            Edit
                                        </button>
                                    )
                                }
                            >
                                <input
                                    id="coupon-code"
                                    value={draft.code}
                                    readOnly={!draft.codeEdited}
                                    onChange={event => set('code', event.target.value.toUpperCase())}
                                    onBlur={blur('code')}
                                    placeholder="e.g. SUMMER26"
                                    className={`${
                                        showError('code') ? errorInputClass : inputClass
                                    } cpn-font-mono ${
                                        !draft.codeEdited ? 'cpn-bg-neutral-50 cpn-text-neutral-600' : ''
                                    }`}
                                />
                            </Field>
                        )}
                    </div>
                </div>

                <div className="cpn-grid cpn-gap-4 sm:cpn-grid-cols-2">
                    <div data-error={Boolean(showError('couponType'))}>
                        {isEdit ? (
                            <Field id="coupon-type" label="Coupon type" hint={LOCK_HINT}>
                                <ReadOnlyValue>{draft.couponType || '—'}</ReadOnlyValue>
                            </Field>
                        ) : (
                            <Field
                                id="coupon-type"
                                label="Coupon type"
                                required
                                error={showError('couponType')}
                            >
                                <NativeSelect
                                    id="coupon-type"
                                    placeholder="Select a type"
                                    options={COUPON_TYPES.map(type => ({ value: type, label: type }))}
                                    value={draft.couponType}
                                    onBlur={blur('couponType')}
                                    onChange={event => set('couponType', event.target.value)}
                                />
                            </Field>
                        )}
                    </div>
                    <div data-error={Boolean(showError('value'))}>
                        {isEdit ? (
                            <Field id="coupon-value" label="Value" hint={LOCK_HINT}>
                                <ReadOnlyValue>
                                    {draft.value && draft.couponType
                                        ? formatCouponValue(draft.couponType, draft.value)
                                        : '—'}
                                </ReadOnlyValue>
                            </Field>
                        ) : (
                            <Field id="coupon-value" label="Value" required error={showError('value')}>
                                <div className="cpn-relative">
                                    {draft.couponType === TYPE_AMOUNT && (
                                        <span className="cpn-pointer-events-none cpn-absolute cpn-left-3.5 cpn-top-1/2 -cpn-translate-y-1/2 cpn-text-sm cpn-text-neutral-400">
                                            $
                                        </span>
                                    )}
                                    <input
                                        id="coupon-value"
                                        inputMode="decimal"
                                        value={draft.value}
                                        onChange={event => set('value', event.target.value)}
                                        onBlur={blur('value')}
                                        placeholder="Value"
                                        className={`${showError('value') ? errorInputClass : inputClass} ${
                                            draft.couponType === TYPE_AMOUNT ? 'cpn-pl-7' : ''
                                        } ${draft.couponType === TYPE_PERCENTAGE ? 'cpn-pr-8' : ''}`}
                                    />
                                    {draft.couponType === TYPE_PERCENTAGE && (
                                        <span className="cpn-pointer-events-none cpn-absolute cpn-right-3.5 cpn-top-1/2 -cpn-translate-y-1/2 cpn-text-sm cpn-text-neutral-400">
                                            %
                                        </span>
                                    )}
                                </div>
                            </Field>
                        )}
                    </div>
                </div>

                <div data-error={Boolean(showError('startDate') || showError('endDate'))}>
                    <Field
                        id="date-range"
                        label="Start date / End date"
                        required
                        error={showError('startDate') || showError('endDate')}
                    >
                        <RangeInput
                            id="date-range"
                            format="YYYY-MM-DD"
                            value={
                                draft.startDate
                                    ? [
                                          moment.utc(draft.startDate, 'YYYY-MM-DD'),
                                          moment.utc(draft.endDate, 'YYYY-MM-DD'),
                                      ]
                                    : null
                            }
                            onChange={dates => {
                                set('startDate', dates ? dates[0].format('YYYY-MM-DD') : '');
                                set('endDate', dates ? dates[1].format('YYYY-MM-DD') : '');
                                blur('startDate')();
                                blur('endDate')();
                            }}
                            className={
                                showError('startDate') || showError('endDate')
                                    ? errorInputClass
                                    : inputClass
                            }
                        />
                    </Field>
                </div>
            </FormSection>

            <FormSection
                title="Where it applies"
                description={
                    isEdit
                        ? 'What this code can be redeemed against. Locked — retargeting a live coupon re-prices carts already in checkout.'
                        : 'Choose whether this discount is camp-wide or targeted.'
                }
            >
                {isEdit ? (
                    // The resolved names, not the pickers. `record` is what has them — the draft
                    // carries ObjectIds, and a disabled react-select would still read as something
                    // that ought to be clickable.
                    <>
                        <Field id="edit-programs" label="Program Code(s)">
                            <div className="cpn-flex cpn-flex-wrap cpn-gap-1.5">
                                {record?.programname?.length ? (
                                    record.programname.map(pill)
                                ) : (
                                    <ReadOnlyValue>All programs</ReadOnlyValue>
                                )}
                            </div>
                        </Field>
                        <Field id="edit-experiences" label="Additional Experiences">
                            <div className="cpn-flex cpn-flex-wrap cpn-gap-1.5">
                                {record?.additioalExpNames?.length ? (
                                    record.additioalExpNames.map(pill)
                                ) : (
                                    <ReadOnlyValue>None</ReadOnlyValue>
                                )}
                            </div>
                        </Field>
                        <div className="cpn-grid cpn-gap-4 sm:cpn-grid-cols-2">
                            <Field id="edit-category" label="Category">
                                <ReadOnlyValue>{record?.category || 'Any'}</ReadOnlyValue>
                            </Field>
                            <Field id="edit-subcategory" label="Sub Category">
                                <ReadOnlyValue>{record?.subCategoryName || 'Any'}</ReadOnlyValue>
                            </Field>
                        </div>
                        <Field id="edit-programtype" label="Program Type">
                            <ReadOnlyValue>
                                {getProgramType(record?.programType) || 'Any'}
                            </ReadOnlyValue>
                        </Field>
                    </>
                ) : (
                    <div className="cpn-grid cpn-gap-3 sm:cpn-grid-cols-2">
                        <RadioCard
                            name="scope"
                            value="all"
                            checked={draft.scope === 'all'}
                            label="All programs"
                            description="Redeemable against anything at this camp."
                            onChange={value => set('scope', value)}
                        />
                        <RadioCard
                            name="scope"
                            value="specific"
                            checked={draft.scope === 'specific'}
                            label="Specific programs"
                            description="Limit it to what you pick below."
                            onChange={value => set('scope', value)}
                        />
                    </div>
                )}

                {!isEdit && draft.scope === 'specific' && (
                    <div className="cpn-grid cpn-gap-4 cpn-pt-1 sm:cpn-grid-cols-2">
                        <div data-error={Boolean(showError('programs'))}>
                            <Field id="program-codes" label="Program Code(s)" error={showError('programs')}>
                                <CouponSelect
                                    inputId="program-codes"
                                    isMulti
                                    closeMenuOnSelect={false}
                                    placeholder="Select..."
                                    value={draft.programs}
                                    options={programOptions}
                                    onChange={value =>
                                        editDraft(previous => ({
                                            ...previous,
                                            programs: value || [],
                                            // Experiences hang off the programs, so dropping a program has
                                            // to drop the experiences that came with it.
                                            experiences: [],
                                        }))
                                    }
                                />
                            </Field>
                        </div>
                        <Field
                            id="experiences"
                            label="Additional Experiences"
                            hint={
                                draft.programs.length
                                    ? undefined
                                    : 'Pick a program first — experiences belong to a program.'
                            }
                        >
                            <CouponSelect
                                inputId="experiences"
                                isMulti
                                closeMenuOnSelect={false}
                                placeholder="Select..."
                                isDisabled={!draft.programs.length}
                                value={draft.experiences}
                                options={experienceOptions}
                                onChange={value => set('experiences', value || [])}
                            />
                        </Field>
                        <Field id="category" label="Category">
                            <CouponSelect
                                inputId="category"
                                isClearable
                                placeholder="Select..."
                                value={draft.category}
                                options={categoryOptions}
                                onChange={value =>
                                    editDraft(previous => ({
                                        ...previous,
                                        category: value,
                                        subCategory: null,
                                    }))
                                }
                            />
                        </Field>
                        <Field
                            id="sub-category"
                            label="Sub Category"
                            hint={draft.category ? undefined : 'Pick a category first.'}
                        >
                            <CouponSelect
                                inputId="sub-category"
                                isClearable
                                placeholder="Select..."
                                isDisabled={!draft.category}
                                value={draft.subCategory}
                                options={subCategoryOptions}
                                onChange={value => set('subCategory', value)}
                            />
                        </Field>
                        <div className="sm:cpn-col-span-2">
                            <Field id="program-type" label="Program Type">
                                <NativeSelect
                                    id="program-type"
                                    placeholder="Any program type"
                                    options={programTypes}
                                    value={draft.programType}
                                    onChange={event => set('programType', event.target.value)}
                                />
                            </Field>
                        </div>
                    </div>
                )}
            </FormSection>

            <FormSection title="Usage limits" description="Control how often this code can be redeemed.">
                <div className="cpn-grid cpn-gap-4 sm:cpn-grid-cols-2">
                    <div data-error={Boolean(showError('userCode'))}>
                        {isEdit ? (
                            <Field id="user-code" label="User Code" hint={LOCK_HINT}>
                                <ReadOnlyValue>{draft.userCode || 'Any user'}</ReadOnlyValue>
                            </Field>
                        ) : (
                            <Field
                                id="user-code"
                                label="User Code"
                                required={draft.refundable}
                                error={showError('userCode')}
                                hint={
                                    draft.refundable
                                        ? 'Required — the refund is delivered to this user.'
                                        : 'Restrict redemption to one family account.'
                                }
                            >
                                <input
                                    id="user-code"
                                    value={draft.userCode}
                                    onChange={event => set('userCode', event.target.value)}
                                    onBlur={blur('userCode')}
                                    placeholder="User Code"
                                    className={showError('userCode') ? errorInputClass : inputClass}
                                />
                            </Field>
                        )}
                    </div>
                    <div data-error={Boolean(showError('possibleUsage'))}>
                        <Field
                            id="possible-usage"
                            label="Possible Usage"
                            required
                            error={showError('possibleUsage')}
                            hint="Total redemptions allowed across all families."
                        >
                            <input
                                id="possible-usage"
                                inputMode="numeric"
                                value={draft.possibleUsage}
                                onChange={event => set('possibleUsage', event.target.value)}
                                onBlur={blur('possibleUsage')}
                                placeholder="Value"
                                className={showError('possibleUsage') ? errorInputClass : inputClass}
                            />
                        </Field>
                    </div>
                </div>
                <div data-error={Boolean(showError('perUserUsage'))}>
                    <Field
                        id="per-user-usage"
                        label="Per User Usage"
                        error={showError('perUserUsage')}
                        hint="Leave blank for unlimited per family."
                    >
                        <input
                            id="per-user-usage"
                            inputMode="numeric"
                            value={draft.perUserUsage}
                            onChange={event => set('perUserUsage', event.target.value)}
                            onBlur={blur('perUserUsage')}
                            placeholder="Max usage per user"
                            className={showError('perUserUsage') ? errorInputClass : inputClass}
                        />
                    </Field>
                </div>
                <div className="cpn-grid cpn-gap-3 sm:cpn-grid-cols-2">
                    <ToggleRow
                        id="private"
                        label={draft.isPrivate ? 'Private' : 'Public'}
                        description={
                            draft.isPrivate
                                ? 'Shareable by code only — not shown to families browsing the site.'
                                : 'Every platform user sees this in their coupon codes, and is notified when it is created.'
                        }
                        checked={draft.isPrivate}
                        onChange={checked => set('isPrivate', checked)}
                    />
                    <ToggleRow
                        id="refund"
                        label="Refund"
                        description={
                            isEdit
                                ? LOCK_HINT
                                : 'The discount is delivered to one user as a refund. Requires a User Code.'
                        }
                        checked={draft.refundable}
                        disabled={isEdit}
                        onChange={checked => set('refundable', checked)}
                    />
                </div>
            </FormSection>

            {/* Clone's one genuinely new decision, and the reason it is a checkbox rather than an
                assumption: "replace this coupon" and "make me another like it" are both real, and
                only the director knows which one this is. Defaulted by where they came from. */}
            {isClone && coupon && (
                <FormSection
                    title="The original"
                    description={`What happens to ${coupon.code} once this clone is created.`}
                >
                    <ToggleRow
                        id="deactivate-original"
                        label={`Deactivate ${coupon.code}`}
                        description={
                            deactivateOriginal
                                ? 'Families will no longer be able to redeem it. Bookings already made keep their discount.'
                                : 'Leave it live. Both codes will be redeemable, at their own values.'
                        }
                        checked={deactivateOriginal}
                        onChange={setDeactivateOriginal}
                    />
                </FormSection>
            )}
        </div>
    );

    return (
        <Modal
            show={open}
            // Always a function — react-bootstrap calls onHide for Escape and backdrop clicks, and
            // a missing handler warns. Closing is suppressed mid-submit by ignoring the call.
            onHide={() => {
                if (!submitting) onClose();
            }}
            centered
            size="lg"
            backdrop={submitting ? 'static' : true}
            // react-bootstrap portals the dialog to document.body, outside the page root, so the
            // Tailwind scope class has to be repeated here or none of the cpn- utilities resolve.
            dialogClassName="coupon-codes-page coupon_form_modal"
        >
            <div className="cpn-bg-cream">
                <div className="cpn-flex cpn-items-start cpn-justify-between cpn-gap-4 cpn-border-b cpn-border-neutral-200 cpn-bg-white cpn-px-6 cpn-py-5">
                    <div className="cpn-flex cpn-items-start cpn-gap-3">
                        <span className="cpn-flex cpn-h-10 cpn-w-10 cpn-shrink-0 cpn-items-center cpn-justify-center cpn-rounded-xl cpn-bg-brand-50 cpn-text-brand-700">
                            <Ticket className="cpn-h-5 cpn-w-5" aria-hidden="true" />
                        </span>
                        <div>
                            <h2 className="cpn-m-0 cpn-text-lg cpn-font-bold cpn-text-neutral-900">
                                {isEdit && 'Edit Coupon Code'}
                                {isClone && 'Clone Coupon Code'}
                                {!isEdit && !isClone && 'Add Coupon Code'}
                            </h2>
                            <p className="cpn-mb-0 cpn-mt-0.5 cpn-text-sm cpn-text-neutral-500">
                                {isEdit ? (
                                    <span className="cpn-font-mono cpn-font-semibold cpn-text-neutral-700">
                                        {coupon?.code}
                                    </span>
                                ) : (
                                    <>
                                        Fields marked{' '}
                                        <span className="cpn-font-semibold cpn-text-brand-600">*</span> are
                                        required.
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        aria-label="Close dialog"
                        className="cpn-rounded-lg cpn-p-2 cpn-text-neutral-400 cpn-transition hover:cpn-bg-neutral-100 hover:cpn-text-neutral-700 disabled:cpn-opacity-50"
                    >
                        <X className="cpn-h-5 cpn-w-5" aria-hidden="true" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                    <div className="cpn-max-h-[62vh] cpn-overflow-y-auto cpn-px-6 cpn-py-5">
                        {/* Edit and clone wait for the record: the form would otherwise flash the
                            empty draft, and a clone submitted early would create a blank coupon. */}
                        {awaitingRecord && (
                            <div className="cpn-flex cpn-justify-center cpn-py-16">
                                <ProgramLoader />
                            </div>
                        )}
                        {fromExisting && !awaitingRecord && !record && (
                            <p className="cpn-py-16 cpn-text-center cpn-text-sm cpn-text-neutral-500">
                                Could not load this coupon’s details.
                            </p>
                        )}
                        {fromExisting && !awaitingRecord && record && renderForm()}
                        {!fromExisting && renderForm()}
                    </div>

                    <div className="cpn-border-t cpn-border-neutral-200 cpn-bg-white cpn-px-6 cpn-py-4">
                        <div className="cpn-flex cpn-flex-col cpn-gap-4 sm:cpn-flex-row sm:cpn-items-center sm:cpn-justify-between">
                            {/* The live preview, in both modes. In edit it is what shows the effect
                                of a date or limit change before it is saved. */}
                            <div className="cpn-flex cpn-items-start cpn-gap-2 cpn-rounded-xl cpn-bg-neutral-50 cpn-px-3 cpn-py-2.5 cpn-text-xs cpn-leading-relaxed cpn-text-neutral-600">
                                    <CheckCircle2
                                        className="cpn-mt-0.5 cpn-h-4 cpn-w-4 cpn-shrink-0 cpn-text-brand-600"
                                        aria-hidden="true"
                                    />
                                    <p className="cpn-m-0" aria-live="polite">
                                        <span className="cpn-font-mono cpn-font-semibold cpn-text-neutral-900">
                                            {draft.code || 'YOURCODE'}
                                        </span>{' '}
                                        gives{' '}
                                        <span className="cpn-font-semibold cpn-text-neutral-900">
                                            {draft.value && draft.couponType
                                                ? formatCouponValue(draft.couponType, draft.value)
                                                : 'no discount yet'}
                                        </span>{' '}
                                        on {scopeSummary},{' '}
                                        {formatDateRange(draft.startDate, draft.endDate).toLowerCase()} ·{' '}
                                        {draft.possibleUsage
                                            ? `${draft.possibleUsage} total uses`
                                            : 'no usage cap'}
                                        {draft.perUserUsage ? `, ${draft.perUserUsage} per family` : ''}
                                        {draft.isPrivate ? ' · private' : ' · public'}
                                    </p>
                            </div>

                            <div className="cpn-flex cpn-flex-col-reverse cpn-gap-3 sm:cpn-flex-row sm:cpn-shrink-0">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    disabled={submitting}
                                    className="cpn-h-11 cpn-rounded-xl cpn-border cpn-border-neutral-200 cpn-px-5 cpn-text-sm cpn-font-semibold cpn-text-neutral-700 cpn-transition hover:cpn-bg-neutral-50 disabled:cpn-opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    // Nothing to save until the record has loaded, and submitting
                                    // the empty draft would blank the coupon's name.
                                    disabled={submitting || (fromExisting && (awaitingRecord || !record))}
                                    className="cpn-h-11 cpn-rounded-xl cpn-bg-brand-700 cpn-px-6 cpn-text-sm cpn-font-semibold cpn-text-white cpn-transition hover:cpn-bg-brand-800 focus:cpn-outline-none focus-visible:cpn-ring-4 focus-visible:cpn-ring-brand-200 disabled:cpn-opacity-60"
                                >
                                    {isEdit && (submitting ? 'Saving…' : 'Save changes')}
                                    {isClone && (submitting ? 'Creating…' : 'Create clone')}
                                    {!isEdit && !isClone && (submitting ? 'Creating…' : 'Create coupon')}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default CouponFormModal;
